import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Renders the Preview component.
 *
 * @returns The rendered Preview component.
 *
 * @date 06/26/2026
 */
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import NavBar from '../NavBar/NavBar';
import { readProcessFlowViewerOrigin } from '../../../services/envConfig';
import { resolveToAbsoluteUrl, isEmbedUrlAllowed } from '../../../utils/permitDetailsEmbed';
import { useEnvConfig } from '../../../utils/useEnvConfig';
import { useFlowViewerHost } from './FlowViewerHost';
const FlowViewerPage = () => {
    const navigate = useNavigate();
    const { id, version, add } = useParams();
    const [searchParams] = useSearchParams();
    const iframeRef = useRef(null);
    const host = useFlowViewerHost();
    const slotRef = useRef(null);
    const env = useEnvConfig();
    // Derived from the same env as the allow-list check below, so the two are ready in the same render.
    const base = useMemo(() => {
        const rawPreview = (env && (env.ProcessPreviewURL || env.Processpreviewurl || env.processPreviewURL || env.processPreviewUrl)) ||
            undefined;
        if (typeof rawPreview === 'string' && rawPreview.trim())
            return rawPreview.trim().replace(/\/$/, '');
        return (readProcessFlowViewerOrigin(env) || '').replace(/\/$/, '');
    }, [env]);
    const pathParam = searchParams.get('path') || '';
    const embedSrc = useMemo(() => {
        const bp = base || '';
        const p = (pathParam || '').trim();
        if (p) {
            // `?path=` is user-controlled: malformed percent-encoding would make decodeURIComponent throw during
            // render. Such a value is used as given and unresolved, so the allow-list check below refuses it (and
            // navigates home) unless it is already a URL on an allowed origin.
            let decoded;
            try {
                decoded = decodeURIComponent(p);
            }
            catch {
                return p;
            }
            // normalize any repeated /processflows segments
            const normalized = decoded.replace(/(\/processflows)+/gi, '/ProcessFlows');
            try {
                if (/^https?:\/\//i.test(normalized))
                    return resolveToAbsoluteUrl(normalized);
            }
            catch { }
            // if base contains hash, attach after origin
            if (bp) {
                if (bp.includes('#')) {
                    const originOnly = bp.replace(/#.*$/, '');
                    const withLeading = normalized.startsWith('/') ? normalized : '/' + normalized;
                    return `${originOnly}#${withLeading}`;
                }
                const withLeading = normalized.startsWith('/') ? normalized : '/' + normalized;
                return `${bp}${withLeading}`;
            }
            return normalized.startsWith('/') ? normalized : '/' + normalized;
        }
        // Build from route params when no path param provided
        if (id) {
            const ver = version || '';
            const addSeg = add !== undefined ? `/${add}` : '';
            // Always use hash-based routing so the shared FlowViewerHost iframe (a HashRouter app)
            // receives a fragment navigation rather than a full-page reload.
            const originBase = bp ? bp.replace(/#.*$/, '').replace(/\/$/, '') : '';
            const flowPath = `/ProcessFlows/${id}${ver ? `/${ver}` : ''}${addSeg}`;
            if (originBase) {
                return `${originBase}/#${flowPath}`;
            }
            return `#${flowPath}`;
        }
        return bp || '';
    }, [base, pathParam, id, version, add]);
    useEffect(() => {
        if (!embedSrc || env === undefined)
            return;
        if (!isEmbedUrlAllowed(embedSrc, env)) {
            const t = setTimeout(() => navigate('/', { replace: true }), 800);
            return () => clearTimeout(t);
        }
    }, [embedSrc, env, navigate]);
    const canEmbed = Boolean(embedSrc && env !== undefined && isEmbedUrlAllowed(embedSrc, env));
    const frameOrigin = useMemo(() => {
        if (!canEmbed)
            return '';
        try {
            return new URL(embedSrc).origin;
        }
        catch {
            return '';
        }
    }, [canEmbed, embedSrc]);
    // No token postMessage required from CMS — iframe should manage its own auth.
    /**
     * Reuse the app's pre-booted Flow Viewer iframe (FlowViewerHost) — same pattern as PermitDetailsViewPage.
     * Positions the shared iframe over the slot div and navigates it to `embedSrc`.
     */
    useLayoutEffect(() => {
        if (!host || !canEmbed)
            return;
        const el = slotRef.current;
        if (!el)
            return;
        host.present(el, embedSrc);
    }, [host, canEmbed, embedSrc]);
    /** Release the shared iframe on unmount so it returns to its hidden/warm state. */
    useEffect(() => {
        return () => host?.release();
    }, [host]);
    // Listen to route updates from iframe and reflect in querystring (matches MPS behavior)
    useEffect(() => {
        const handleMessage = (event) => {
            try {
                if (!frameOrigin || event.origin !== frameOrigin)
                    return;
                if (!event?.data || typeof event.data !== 'object')
                    return;
                if (event.data.type !== 'IFRAME_ROUTE_UPDATE')
                    return;
                const path = event.data.path;
                if (!path)
                    return;
                const current = new URLSearchParams(window.location.search).get('path');
                if (current === path)
                    return;
                navigate(`?path=${encodeURIComponent(path)}`, { replace: true });
            }
            catch {
                // ignore
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [navigate, frameOrigin]);
    return (_jsxs(Box, { sx: { height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }, children: [_jsx(Box, { sx: { width: '100%', flexShrink: 0 }, children: _jsx(NavBar, { links: [] }) }), _jsxs(Box, { sx: { flex: 1, minHeight: 0 }, children: [!embedSrc ? (_jsx(Box, { sx: { p: 2 }, children: "No flow viewer path provided." })) : null, embedSrc && env !== undefined && !canEmbed ? (_jsx(Box, { sx: { p: 2 }, children: "Cannot show the requested flow here." })) : null, canEmbed ? (_jsx(Box, { ref: slotRef, sx: { flex: 1, minHeight: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }, children: !host ? (_jsx("iframe", { title: "Flow Viewer", ref: iframeRef, src: embedSrc, style: { width: '100%', height: '100%', border: 'none' } })) : null })) : null] })] }));
};
export default FlowViewerPage;
